import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import { UnsavedChangesModal } from "./UnsavedChangesModal";

type UnsavedChangesGuardContextType = {
  setHasUnsavedChanges: (value: boolean) => void;
  runWithUnsavedGuard: (action: () => void) => void;
};

const UnsavedChangesGuardContext =
  createContext<UnsavedChangesGuardContextType | null>(null);

export const useUnsavedChangesGuard = () => {
  const ctx = useContext(UnsavedChangesGuardContext);
  if (!ctx) {
    throw new Error(
      "useUnsavedChangesGuard must be used inside UnsavedChangesGuardProvider",
    );
  }
  return ctx;
};

type ProviderProps = {
  children: ReactNode;
  message?: string;
};

export const UnsavedChangesGuardProvider: React.FC<ProviderProps> = ({
  children,
  message = "Do you want to leave without savings?",
}) => {
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const setHasUnsavedChanges = useCallback((value: boolean) => {
    setHasUnsavedChangesState(value);
  }, []);

  const runWithUnsavedGuard = useCallback(
    (action: () => void) => {
      if (!hasUnsavedChanges) {
        action();
        return;
      }
      setPendingAction(() => action);
    },
    [hasUnsavedChanges],
  );

  const handleConfirm = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action) {
      action();
    }
  };

  const handleCancel = () => {
    setPendingAction(null);
  };

  return (
    <UnsavedChangesGuardContext.Provider
      value={{ setHasUnsavedChanges, runWithUnsavedGuard }}>
      {children}

      <UnsavedChangesModal
        open={!!pendingAction}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </UnsavedChangesGuardContext.Provider>
  );
};
